import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "rsuite";

import { searchCustomItems } from "../../../../../services/table.services.js";
import type {
  CustomItemDraft,
  CustomItemVariation,
  CustomTaxType,
} from "../hooks/types.ts";

type NameField = "english" | "japanese";

interface CustomItemSuggestion {
  _id: string;
  item_name?: string | null;
  item_name_ja?: string | null;
  total_price?: number | string | null;
  tax_type: CustomTaxType;
  consumption_tax_rate: number;
  item_variation: CustomItemVariation;
}

interface CustomItemModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (draft: CustomItemDraft) => void | Promise<void>;
  isSaving?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

const emptyDraft = (): CustomItemDraft => ({
  item_name: "",
  item_name_ja: "",
  total_price: "",
  quantity: 1,
  tax_type: "dine",
  item_variation: "food",
});

const CustomItemModal: React.FC<CustomItemModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<CustomItemDraft>(emptyDraft);
  const [activeNameField, setActiveNameField] = useState<NameField>("english");
  const [suggestions, setSuggestions] = useState<CustomItemSuggestion[]>([]);
  const [nameError, setNameError] = useState("");
  const [priceError, setPriceError] = useState("");
  const suppressSearchRef = useRef(false);
  const englishFieldRef = useRef<HTMLDivElement>(null);
  const japaneseFieldRef = useRef<HTMLDivElement>(null);

  const updateDraft = <K extends keyof CustomItemDraft>(key: K, value: CustomItemDraft[K]) => {
    setDraft(current => ({ ...current, [key]: value }));
    if (key === "item_name" || key === "item_name_ja") setNameError("");
    if (key === "total_price") setPriceError("");
  };

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!englishFieldRef.current?.contains(target) && !japaneseFieldRef.current?.contains(target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const searchTerm = (activeNameField === "english" ? draft.item_name : draft.item_name_ja).trim();
    const minimumLength = activeNameField === "japanese" ? 1 : 2;

    if (searchTerm.length < minimumLength) {
      setSuggestions([]);
      return undefined;
    }

    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      return undefined;
    }

    let isCurrentRequest = true;
    let requestController: AbortController | undefined;
    const timeoutId = window.setTimeout(async () => {
      requestController = new AbortController();
      try {
        const response = await searchCustomItems({
          query: searchTerm,
          field: activeNameField,
          limit: SEARCH_LIMIT,
          signal: requestController.signal,
        });

        if (isCurrentRequest) {
          setSuggestions(response?.data?.list || []);
        }
      } catch (requestError: unknown) {
        const errorDetails = requestError as { code?: string; name?: string };
        if (
          isCurrentRequest
          && errorDetails.code !== "ERR_CANCELED"
          && errorDetails.name !== "CanceledError"
        ) {
          setSuggestions([]);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timeoutId);
      requestController?.abort();
    };
  }, [activeNameField, draft.item_name, draft.item_name_ja, open]);

  useEffect(() => {
    if (!open) {
      setDraft(emptyDraft());
      setActiveNameField("english");
      setSuggestions([]);
      setNameError("");
      setPriceError("");
    }
  }, [open]);

  const handleNameFocus = (field: NameField) => {
    setActiveNameField(field);
    setSuggestions([]);
  };

  const applySuggestion = (suggestion: CustomItemSuggestion) => {
    const englishName = suggestion.item_name?.trim() || suggestion.item_name_ja?.trim() || "";
    const japaneseName = suggestion.item_name_ja?.trim() || suggestion.item_name?.trim() || "";

    suppressSearchRef.current = true;
    setDraft(current => ({
      ...current,
      item_name: englishName,
      item_name_ja: japaneseName,
      total_price: String(suggestion.total_price ?? 0),
      tax_type: suggestion.tax_type,
      item_variation: suggestion.item_variation,
    }));
    setSuggestions([]);
    setNameError("");
    setPriceError("");
  };

  const formatPrice = (value: number | string | null | undefined) => (
    `¥${Number(value || 0).toLocaleString("ja-JP")}`
  );

  const taxLabel = (suggestion: CustomItemSuggestion) => (
    suggestion.tax_type === "take_out"
      ? t("custom_item_tax_takeout_short", { rate: suggestion.consumption_tax_rate })
      : t("custom_item_tax_dine_short", { rate: suggestion.consumption_tax_rate })
  );

  const handleSubmit = async (): Promise<void> => {
    const hasName = Boolean(draft.item_name.trim() || draft.item_name_ja.trim());
    const price = Number(draft.total_price);
    const hasWholeYenPrice = draft.total_price.trim() !== "" && Number.isInteger(price) && price >= 0;

    setNameError(hasName ? "" : t("custom_item_name_required"));
    setPriceError(hasWholeYenPrice ? "" : t("custom_item_price_validation_error"));

    if (!hasName || !hasWholeYenPrice) {
      return;
    }

    await onSubmit?.({
      ...draft,
      item_name: draft.item_name.trim() || draft.item_name_ja.trim(),
      item_name_ja: draft.item_name_ja.trim() || draft.item_name.trim(),
      total_price: String(price),
    });
  };

  const handleClose = () => {
    setDraft(emptyDraft());
    setSuggestions([]);
    setNameError("");
    setPriceError("");
    onClose();
  };

  const renderSuggestion = (suggestion: CustomItemSuggestion) => {
    const displayName = activeNameField === "japanese"
      ? suggestion.item_name_ja?.trim() || suggestion.item_name?.trim()
      : suggestion.item_name?.trim() || suggestion.item_name_ja?.trim();

    return (
      <button
        type="button"
        className="custom-item-suggestion"
        key={suggestion._id}
        onClick={() => applySuggestion(suggestion)}
      >
        <i
          className={`custom-item-suggestion-icon ${suggestion.item_variation === "food" ? "fa fa-utensils" : "fa fa-glass"}`}
          aria-hidden="true"
        />
        <span className="custom-item-suggestion-copy">
          <span className="custom-item-suggestion-name">{displayName}</span>
          <span className="custom-item-suggestion-tax">{taxLabel(suggestion)}</span>
        </span>
        <strong className="custom-item-suggestion-price">{formatPrice(suggestion.total_price)}</strong>
      </button>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      backdrop="static"
      className="custom-item-modal"
      size="sm"
      aria-labelledby="custom-item-modal-title"
    >
      <Modal.Header>
        <div className="custom-item-modal-header">
          <Modal.Title id="custom-item-modal-title">{t("label_add_custom_item")}</Modal.Title>
          <button
            type="button"
            className="custom-item-modal-close"
            onClick={handleClose}
            aria-label={t("custom_item_close")}
          >
            ×
          </button>
        </div>
      </Modal.Header>
      <div className="custom-item-form" onClick={event => event.stopPropagation()}>
        <Modal.Body>
          <div className="custom-item-field-grid custom-item-name-field-grid">
            <NameFieldInput
              ref={englishFieldRef}
              id="custom-item-name"
              label={t("custom_item_english_name")}
              placeholder={t("custom_item_english_name_placeholder")}
              value={draft.item_name}
              onFocus={() => handleNameFocus("english")}
              onChange={value => updateDraft("item_name", value)}
              suggestions={activeNameField === "english" ? suggestions : []}
              renderSuggestion={renderSuggestion}
              autoFocus
              errorId={nameError ? "custom-item-name-error" : undefined}
            />
            <NameFieldInput
              ref={japaneseFieldRef}
              id="custom-item-name-ja"
              label={t("custom_item_japanese_name")}
              placeholder={t("custom_item_japanese_name_placeholder")}
              value={draft.item_name_ja}
              onFocus={() => handleNameFocus("japanese")}
              onChange={value => updateDraft("item_name_ja", value)}
              suggestions={activeNameField === "japanese" ? suggestions : []}
              renderSuggestion={renderSuggestion}
              errorId={nameError ? "custom-item-name-error" : undefined}
            />
          </div>
          <div className="custom-item-validation-slot custom-item-name-validation-slot">
            {nameError ? <span id="custom-item-name-error" className="custom-item-error" role="alert">{nameError}</span> : null}
          </div>
          <Field
            id="custom-item-price"
            label={t("custom_item_price_label")}
            type="number"
            min={0}
            step={1}
            value={draft.total_price}
            onFocus={() => setSuggestions([])}
            onChange={value => updateDraft("total_price", value)}
            errorId={priceError ? "custom-item-price-error" : undefined}
            className="custom-item-price-field"
          />
          <div className="custom-item-validation-slot custom-item-price-validation-slot">
            {priceError ? <span id="custom-item-price-error" className="custom-item-error" role="alert">{priceError}</span> : null}
          </div>

          <div className="custom-item-field-grid">
            <Choice
              label={t("custom_item_tax_classification")}
              options={[
                ["dine", t("custom_item_tax_dine")],
                ["take_out", t("custom_item_tax_takeout")],
              ]}
              value={draft.tax_type}
              onChange={value => updateDraft("tax_type", value as CustomTaxType)}
            />
            <Choice
              label={t("custom_item_kitchen_route")}
              options={[
                ["food", t("custom_item_food")],
                ["drink", t("custom_item_drink")],
              ]}
              value={draft.item_variation}
              onChange={value => updateDraft("item_variation", value as CustomItemVariation)}
              withIcons
            />
          </div>
          <div className="custom-item-total-row">
            <span>{t("label_total")}</span>
            <strong>{formatPrice(draft.total_price)}</strong>
          </div>
        </Modal.Body>
        <div className="custom-item-modal-actions">
          <button type="button" className="btn btn-default" onClick={handleClose} disabled={isSaving}>
            {t("custom_item_cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isSaving}
            onClick={() => void handleSubmit()}
          >
            {isSaving ? t("custom_item_adding") : t("custom_item_add_to_order")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface FieldProps {
  id: string;
  label: string;
  placeholder?: string;
  type?: string;
  min?: number;
  step?: number;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  errorId?: string;
  className?: string;
}

const Field = ({ id, label, placeholder, type = "text", min, step, value, onChange, onFocus, errorId, className }: FieldProps) => (
  <div className={`custom-item-field-group ${className || ""}`.trim()}>
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      className="form-control"
      type={type}
      min={min}
      step={step}
      placeholder={placeholder}
      value={value}
      onFocus={onFocus}
      onChange={event => onChange(event.target.value)}
      aria-describedby={errorId}
    />
  </div>
);

interface NameFieldInputProps extends FieldProps {
  suggestions: CustomItemSuggestion[];
  renderSuggestion: (suggestion: CustomItemSuggestion) => React.ReactNode;
  autoFocus?: boolean;
  errorId?: string;
}

const NameFieldInput = React.forwardRef<HTMLDivElement, NameFieldInputProps>(
  ({ id, label, placeholder, value, onChange, onFocus, suggestions, renderSuggestion, autoFocus, errorId }, ref) => (
    <div className="custom-item-field-group custom-item-name-field" ref={ref}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="form-control"
        type="text"
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        aria-describedby={errorId}
        onFocus={onFocus}
        onChange={event => onChange(event.target.value)}
      />
      {suggestions.length > 0 ? (
        <div className="custom-item-suggestion-dropdown" role="listbox">
          {suggestions.map(renderSuggestion)}
        </div>
      ) : null}
    </div>
  ),
);

NameFieldInput.displayName = "NameFieldInput";

interface ChoiceProps {
  label: string;
  options: [string, string][];
  value: string;
  onChange: (value: string) => void;
  withIcons?: boolean;
}

const Choice = ({ label, options, value, onChange, withIcons = false }: ChoiceProps) => (
  <fieldset className="custom-item-field-group custom-item-choice-group">
    <legend>{label}</legend>
    <div className="custom-item-segmented-control">
      {options.map(([optionValue, optionLabel]) => (
        <button
          type="button"
          key={optionValue}
          className={value === optionValue ? "active" : ""}
          onClick={() => onChange(optionValue)}
        >
          {withIcons ? (
            <i
              className={`custom-item-choice-icon ${optionValue === "food" ? "fa fa-utensils" : "fa fa-glass"}`}
              aria-hidden="true"
            />
          ) : null}
          {optionLabel}
        </button>
      ))}
    </div>
  </fieldset>
);

export default CustomItemModal;
